import { plusBusinessDays } from '@/helpers/dates';
import { t } from '@/i18n';
import ContractEvent, { ContractEventStatus } from '@/models/ContractEvent';
import Delay, { DelayStatus } from '@/models/Delay';
import Project from '@/models/Project';
import VariationOrder, { VariationOrderStatus } from '@/models/VariationOrder';

export type CenScheduleTimeBar = {
  text: string;
  colour?: string;
};

function dueInColour(dueIn: number): string | undefined {
  if(dueIn < 0 || dueIn === 0 || dueIn < 4) {
    return '#dc2626';
  }

  if(dueIn < 6) {
    return '#ea580c';
  }

  return undefined;
}

function remainingLabel(dueIn: number, overdueKey: string, dueTodayKey: string, daysRemainingKey: string): string {
  if(dueIn < 0) {
    return t(overdueKey, { days: Math.abs(dueIn) });
  }

  if(dueIn === 0) {
    return t(dueTodayKey);
  }

  return t(daysRemainingKey, { days: dueIn });
}

function buildRaiseTimeBar(contractEvent: ContractEvent, project: Project): CenScheduleTimeBar | undefined {
  const settings = project.head_contract_settings;

  if(!settings.cen_raise_required || !contractEvent.dateOfOccurrence) {
    return undefined;
  }

  const dueDate = settings.cen_raise_business_days ?
    plusBusinessDays(
      contractEvent.dateOfOccurrence,
      settings.holidays,
      settings.cen_raise_period,
    ) :
    contractEvent.dateOfOccurrence.plus({ days: settings.cen_raise_period });

  const dueIn = Math.ceil(dueDate.diffNow('days').days);

  const prefix = dueIn < 0 ?
    `${t('HeadContract.CenSchedule.timeBarLabels.timeBarred')} - ` :
    '';

  const text = `${prefix}${t('HeadContract.CenSchedule.timeBarLabels.raiseToVoDelay')} ${dueDate.toLocaleString()}, ${
    remainingLabel(
      dueIn,
      'HeadContract.CenSchedule.timeBarLabels.overdueBy',
      'HeadContract.CenSchedule.timeBarLabels.dueToday',
      'HeadContract.CenSchedule.timeBarLabels.daysRemaining',
    )
  }.`;

  return {
    text,
    colour: dueInColour(dueIn),
  };
}

function buildVariationOrderTimeBar(variationOrder: VariationOrder, project: Project): CenScheduleTimeBar | undefined {
  const settings = project.head_contract_settings;

  if(!variationOrder.dateOfOccurrence || !variationOrder.requiresNotice) {
    return undefined;
  }

  if(!variationOrder.notice_sent_at && settings.nov_required) {
    const dueDate = settings.nov_business_days ?
      plusBusinessDays(variationOrder.dateOfOccurrence, settings.holidays, settings.nov_period) :
      variationOrder.dateOfOccurrence.plus({ days: settings.nov_period });

    const dueIn = Math.ceil(dueDate.diffNow('days').days);

    const prefix = dueIn < 0 ?
      `${t('HeadContract.VariationsSchedule.VariationOrderRow.timeBarred')} - ` :
      '';

    return {
      text: `${prefix}${
        t('HeadContract.VariationsSchedule.VariationOrderRow.noticeOfVariation')
      } ${dueDate.toLocaleString()}, ${
        remainingLabel(
          dueIn,
          'HeadContract.VariationsSchedule.VariationOrderRow.overdueBy',
          'HeadContract.VariationsSchedule.VariationOrderRow.dueToday',
          'HeadContract.VariationsSchedule.VariationOrderRow.daysRemaining',
        )
      }.`,

      colour: dueInColour(dueIn),
    };
  }

  if(
    variationOrder.status < VariationOrderStatus.submitted &&
    settings.variation_submission_required
  ) {
    const dueDate = settings.variation_submission_business_days ?
      plusBusinessDays(
        variationOrder.dateOfOccurrence,
        settings.holidays,
        settings.variation_submission_period,
      ) :
      variationOrder.dateOfOccurrence.plus({ days: settings.variation_submission_period });

    const dueIn = Math.ceil(dueDate.diffNow('days').days);

    const prefix = dueIn < 0 ?
      `${t('HeadContract.VariationsSchedule.VariationOrderRow.timeBar.timeBarred')} - ` :
      '';

    return {
      text: `${prefix}${
        t('HeadContract.VariationsSchedule.VariationOrderRow.variationPricingToBeIssuedBy')
      } ${dueDate.toLocaleString()}, ${
        remainingLabel(
          dueIn,
          'HeadContract.VariationsSchedule.VariationOrderRow.overdueBy',
          'HeadContract.VariationsSchedule.VariationOrderRow.dueToday',
          'HeadContract.VariationsSchedule.VariationOrderRow.daysRemaining',
        )
      }.`,

      colour: dueInColour(dueIn),
    };
  }

  return undefined;
}

function buildDelayTimeBar(delay: Delay, project: Project): CenScheduleTimeBar | undefined {
  const settings = project.head_contract_settings;

  if(!delay.dateOfOccurrence) {
    return undefined;
  }

  if(!delay.notice_sent_at && settings.nod_required) {
    const dueDate = settings.nod_business_days ?
      plusBusinessDays(delay.dateOfOccurrence, settings.holidays, settings.nod_period) :
      delay.dateOfOccurrence.plus({ days: settings.nod_period });

    const dueIn = Math.ceil(dueDate.diffNow('days').days);

    const prefix = dueIn < 0 ?
      `${t('HeadContract.DelaysSchedule.DelayRow.timeBarred')} - ` :
      '';

    return {
      text: `${prefix}${
        t('HeadContract.DelaysSchedule.DelayRow.timeBar.noticeOfDelayToBeIssuedBy')
      } ${dueDate.toLocaleString()}, ${
        remainingLabel(
          dueIn,
          'HeadContract.DelaysSchedule.DelayRow.timeBar.overdueBy',
          'HeadContract.DelaysSchedule.DelayRow.timeBar.dueToday',
          'HeadContract.DelaysSchedule.DelayRow.timeBar.daysRemaining',
        )
      }.`,

      colour: dueInColour(dueIn),
    };
  }

  if(
    delay.status < DelayStatus.submitted &&
    settings.delay_submission_required
  ) {
    const dueDate = settings.delay_submission_business_days ?
      plusBusinessDays(
        delay.dateOfOccurrence,
        settings.holidays,
        settings.delay_submission_period,
      ) :
      delay.dateOfOccurrence.plus({ days: settings.delay_submission_period });

    const dueIn = Math.ceil(dueDate.diffNow('days').days);

    const prefix = dueIn < 0 ?
      `${t('HeadContract.DelaysSchedule.DelayRow.timeBar.timeBarred')} - ` :
      '';

    return {
      text: `${prefix}${
        t('HeadContract.DelaysSchedule.DelayRow.timeBar.extensionOfTimeSubmissionToBeIssuedBy')
      } ${dueDate.toLocaleString()}, ${
        remainingLabel(
          dueIn,
          'HeadContract.DelaysSchedule.DelayRow.timeBar.overdueBy',
          'HeadContract.DelaysSchedule.DelayRow.timeBar.dueToday',
          'HeadContract.DelaysSchedule.DelayRow.timeBar.daysRemaining',
        )
      }.`,

      colour: dueInColour(dueIn),
    };
  }

  return undefined;
}

export function getCenScheduleTimeBars(
  contractEvent: ContractEvent,
  project: Project | undefined,
): CenScheduleTimeBar[] {
  if(!project?.unarchived || contractEvent.status === ContractEventStatus.declined) {
    return [];
  }

  if(contractEvent.status === ContractEventStatus.pending) {
    const raiseBar = buildRaiseTimeBar(contractEvent, project);

    return raiseBar ? [raiseBar] : [];
  }

  const bars: CenScheduleTimeBar[] = [];

  if(
    (
      contractEvent.status === ContractEventStatus.registered_as_variation_order ||
      contractEvent.status === ContractEventStatus.registered_as_variation_order_and_delay
    ) &&
    contractEvent.variation_order
  ) {
    const voBar = buildVariationOrderTimeBar(contractEvent.variation_order, project);

    if(voBar) {
      bars.push(voBar);
    }
  }

  if(
    (
      contractEvent.status === ContractEventStatus.registered_as_delay ||
      contractEvent.status === ContractEventStatus.registered_as_variation_order_and_delay
    ) &&
    contractEvent.delay
  ) {
    const delayBar = buildDelayTimeBar(contractEvent.delay, project);

    if(delayBar) {
      bars.push(delayBar);
    }
  }

  return bars;
}
